import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Play } from 'lucide-react';
import SEO from '../components/SEO';
import RegionSelector from '../components/trending/RegionSelector';
import ShowHero from '../components/show/ShowHero';
import WhereToWatch from '../components/show/WhereToWatch';
import SeasonsBlock from '../components/show/SeasonsBlock';
import CastStrip from '../components/show/CastStrip';
import RelatedShows from '../components/show/RelatedShows';
import TrailerModal from '../components/show/TrailerModal';
import PublicShowSkeleton from '../components/show/PublicShowSkeleton';
import detectRegion, { REGION_LABEL_SHORT } from '../utils/detectRegion';
import { REGION_LABEL_LONG, normaliseRegion } from '../utils/regions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REDIRECT_URL = window.location.origin + '/dashboard';
const AUTH_URL = `${process.env.REACT_APP_AUTH_URL}/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

const PublicShow = ({ user }) => {
  const navigate = useNavigate();
  const { tmdbId } = useParams();
  const [search, setSearch] = useSearchParams();
  const isLoggedIn = !!user;

  const initialRegion =
    normaliseRegion(search.get('region')) ||
    detectRegion(user && user.region) ||
    'global';

  const [region, setRegion] = useState(initialRegion);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [trailerOpen, setTrailerOpen] = useState(false);

  const load = useCallback(async (id, r) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/public/show/${id}`, {
        params: { region: r },
      });
      setData(resp.data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setError('We couldn\'t find that show — it may have been removed from TMDB.');
      } else {
        setError(
          (e.response && e.response.data && e.response.data.detail) ||
            'Something went wrong loading this show. Please try again in a moment.'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!tmdbId) return;
    load(tmdbId, region);
    // scroll to top when show or region changes
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
  }, [tmdbId, region, load]);

  const handleRegionChange = (next) => {
    setRegion(next);
    if (next === 'global') {
      const params = new URLSearchParams(search);
      params.delete('region');
      setSearch(params, { replace: true });
    } else {
      const params = new URLSearchParams(search);
      params.set('region', next);
      setSearch(params, { replace: true });
    }
  };

  const handleSignIn = () => {
    window.location.href = AUTH_URL;
  };

  const handleTrack = () => {
    if (!data || !data.show) return;
    navigate(`/my-shows?add=${data.show.tmdb_id}`);
  };

  const show = data && data.show;
  const regionLabel = REGION_LABEL_LONG[region] || 'the world';
  const regionShort = REGION_LABEL_SHORT[region] || regionLabel;

  // SEO bits
  const pageTitle = show
    ? `${show.title}${show.year ? ` (${show.year})` : ''} — Where to watch in ${regionShort} | baad.tv`
    : 'Loading show… — baad.tv';
  const pageDescription = show
    ? `${show.overview ? show.overview.slice(0, 160) : `Watch ${show.title} on baad.tv.`} See cast, seasons, and where to stream${region !== 'global' ? ` in ${regionShort}` : ''}.`
    : 'Discover where to watch this show on baad.tv.';

  // Backdrop is wider — better social-card aspect. Fall back to poster.
  const pageImage = (show && (show.backdrop_url || show.image_url)) || undefined;
  const canonicalPath = show
    ? (region === 'global'
        ? `/show/${show.tmdb_id}`
        : `/show/${show.tmdb_id}?region=${region}`)
    : '/';

  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previous;
    };
  }, [pageTitle]);

  // JSON-LD TVSeries schema
  useEffect(() => {
    if (!show) return undefined;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'TVSeries',
      name: show.title,
      description: show.overview || undefined,
      image: show.image_url || undefined,
      datePublished: show.first_air_date || undefined,
      genre: (show.genres || []).map((g) => g.name),
      numberOfSeasons: show.number_of_seasons || undefined,
      numberOfEpisodes: show.number_of_episodes || undefined,
      url: show.tmdb_url,
      aggregateRating: show.vote_average
        ? {
            '@type': 'AggregateRating',
            ratingValue: show.vote_average,
            ratingCount: show.vote_count || 1,
            bestRating: 10,
            worstRating: 0,
          }
        : undefined,
    };
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-show-jsonld', String(show.tmdb_id));
    tag.text = JSON.stringify(jsonLd);
    document.head.appendChild(tag);
    return () => {
      try { document.head.removeChild(tag); } catch (e) { /* noop */ }
    };
  }, [show]);

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        image={pageImage}
        url={canonicalPath}
        type="video.tv_show"
      />

      <div className="show-page">
        {/* Mini public nav (matches /trending) */}
        <nav className="public-nav" data-testid="public-show-nav">
          <a
            className="brand"
            href="/"
            data-testid="public-show-nav-brand"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            <img src="/sheep-logo.png" alt="baad.tv logo" width="32" height="32" />
            <span>baad.tv</span>
          </a>
          <div className="nav-actions">
            <button
              type="button"
              data-testid="public-show-nav-trending"
              onClick={() => navigate('/trending')}
              className="nav-btn ghost"
            >
              Trending
            </button>
            {isLoggedIn ? (
              <button
                type="button"
                data-testid="public-show-nav-dashboard"
                onClick={() => navigate('/dashboard')}
                className="nav-btn primary"
              >
                Go to dashboard
              </button>
            ) : (
              <button
                type="button"
                data-testid="public-show-nav-signin"
                onClick={handleSignIn}
                className="nav-btn primary"
              >
                Join the flock
              </button>
            )}
          </div>
        </nav>

        {loading && <PublicShowSkeleton />}

        {error && !loading && (
          <div className="error" data-testid="show-error">
            <p>{error}</p>
            <button
              type="button"
              className="retry-btn"
              onClick={() => load(tmdbId, region)}
              data-testid="show-retry"
            >
              Try again
            </button>
            <button
              type="button"
              className="back-btn"
              onClick={() => navigate('/trending')}
            >
              Back to trending
            </button>
          </div>
        )}

        {show && !loading && !error && (
          <>
            <ShowHero
              show={show}
              region={region}
              regionLabel={regionShort}
            />

            <RegionSelector active={region} onChange={handleRegionChange} />

            <main className="show-body">
              {/* Primary CTA bar */}
              <div className="cta-bar" data-testid="show-cta-bar">
                <button
                  type="button"
                  data-testid="show-primary-cta"
                  className={`cta ${isLoggedIn ? 'cta-outline' : 'cta-solid'}`}
                  onClick={isLoggedIn ? handleTrack : handleSignIn}
                >
                  {isLoggedIn ? 'Track this show' : 'Join the flock'}
                </button>
                {show.trailer && show.trailer.embed_url && (
                  <button
                    type="button"
                    data-testid="show-trailer-btn"
                    className="cta cta-ghost"
                    onClick={() => setTrailerOpen(true)}
                  >
                    <Play size={16} fill="currentColor" />
                    Watch trailer
                  </button>
                )}
                <a
                  href={show.tmdb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tmdb-link"
                  data-testid="show-tmdb-link"
                >
                  View on TMDB ↗
                </a>
              </div>

              {/* Overview */}
              {show.overview && (
                <section className="block" data-testid="show-overview-block">
                  <h2 className="block-title">Overview</h2>
                  <p className="overview-text">{show.overview}</p>
                  {show.created_by && show.created_by.length > 0 && (
                    <p className="created-by">
                      Created by{' '}
                      <strong>{show.created_by.map((c) => c.name).join(', ')}</strong>
                    </p>
                  )}
                </section>
              )}

              <WhereToWatch
                region={region}
                regionLabel={regionShort}
                providers={show.providers}
                isGlobal={region === 'global'}
              />

              <SeasonsBlock show={show} />

              <CastStrip cast={show.cast} />

              <RelatedShows related={show.related} region={region} />
            </main>
          </>
        )}

        <footer className="show-footer">
          <div className="footer-inner">
            <p>Data courtesy of TMDB.</p>
            <div className="footer-links">
              <button onClick={() => navigate('/trending')} data-testid="show-footer-trending">Trending</button>
              <span>•</span>
              <button onClick={() => navigate('/')} data-testid="show-footer-home">Home</button>
            </div>
          </div>
        </footer>

        <style jsx>{`
          .show-page {
            background: #0a0a0a;
            min-height: 100vh;
            color: #fff;
            font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          }
          .public-nav {
            position: sticky;
            top: 0;
            z-index: 50;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.85rem 1.5rem;
            background: rgba(10, 10, 10, 0.7);
            backdrop-filter: blur(18px);
            -webkit-backdrop-filter: blur(18px);
            border-bottom: 1px solid #1a1a1a;
          }
          .brand {
            display: inline-flex;
            align-items: center;
            gap: 0.55rem;
            text-decoration: none;
            color: #E62F88;
            font-weight: 800;
            font-size: 1.25rem;
            letter-spacing: -0.01em;
          }
          .nav-actions {
            display: flex;
            gap: 0.5rem;
          }
          .nav-btn {
            border: 1px solid #2a2a2a;
            background: #161616;
            color: #fff;
            padding: 0.55rem 1rem;
            font-weight: 700;
            font-size: 0.85rem;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.15s, border-color 0.15s, transform 0.15s;
          }
          .nav-btn.ghost {
            background: transparent;
          }
          .nav-btn.ghost:hover {
            background: rgba(230, 47, 136, 0.12);
            border-color: rgba(230, 47, 136, 0.4);
          }
          .nav-btn.primary {
            background: #E62F88;
            color: #000;
            border-color: #E62F88;
          }
          .nav-btn.primary:hover {
            background: #ff4f9e;
            transform: translateY(-1px);
          }
          .show-body {
            max-width: 1100px;
            margin: 0 auto;
            padding: 2rem 1.5rem 4rem;
          }
          .cta-bar {
            display: flex;
            gap: 0.85rem;
            align-items: center;
            margin: 0.5rem 0 2rem;
            flex-wrap: wrap;
          }
          .cta {
            padding: 0.85rem 1.5rem;
            font-size: 0.95rem;
            font-weight: 700;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.15s, border-color 0.15s, transform 0.15s;
          }
          .cta-solid {
            background: #E62F88;
            color: #000;
            border: none;
          }
          .cta-solid:hover {
            background: #ff4f9e;
            transform: translateY(-1px);
          }
          .cta-outline {
            background: transparent;
            color: #fff;
            border: 1px solid #E62F88;
          }
          .cta-outline:hover {
            background: rgba(230, 47, 136, 0.14);
            transform: translateY(-1px);
          }
          .cta-ghost {
            background: transparent;
            color: #fff;
            border: 1px solid #2a2a2a;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
          .cta-ghost:hover {
            border-color: rgba(230, 47, 136, 0.55);
            background: rgba(230, 47, 136, 0.08);
            transform: translateY(-1px);
          }
          .tmdb-link {
            color: #a1a1aa;
            font-size: 0.9rem;
            font-weight: 600;
            text-decoration: none;
          }
          .tmdb-link:hover {
            color: #E62F88;
          }
          :global(.show-page .block) {
            margin: 2rem 0;
          }
          :global(.show-page .block-title) {
            color: #E62F88;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            margin: 0 0 1rem;
          }
          .overview-text {
            color: #c4c4cc;
            font-size: 1rem;
            line-height: 1.7;
            margin: 0 0 1rem;
            max-width: 760px;
          }
          .created-by {
            color: #a1a1aa;
            font-size: 0.85rem;
            margin: 0;
          }
          .created-by strong {
            color: #fff;
            font-weight: 700;
          }
          .loading {
            text-align: center;
            padding: 4rem 1.5rem;
            color: #a1a1aa;
            font-size: 1rem;
          }
          .error {
            max-width: 520px;
            margin: 3rem auto;
            text-align: center;
            padding: 2rem 1.5rem;
            background: #131313;
            border: 1px solid #3a1f2b;
            border-radius: 14px;
            color: #fca5a5;
          }
          .retry-btn,
          .back-btn {
            margin: 1rem 0.5rem 0;
            background: #E62F88;
            color: #000;
            border: none;
            padding: 0.6rem 1.2rem;
            border-radius: 8px;
            font-weight: 700;
            cursor: pointer;
          }
          .back-btn {
            background: transparent;
            color: #fff;
            border: 1px solid #2a2a2a;
          }
          .show-footer {
            margin-top: 4rem;
            border-top: 1px solid #1a1a1a;
            padding: 2.5rem 1.5rem;
          }
          .footer-inner {
            max-width: 1100px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            color: #6f6f78;
            font-size: 0.85rem;
          }
          .footer-links {
            display: flex;
            align-items: center;
            gap: 0.65rem;
          }
          .footer-links button {
            background: none;
            border: none;
            color: #E62F88;
            cursor: pointer;
            font-size: 0.85rem;
            text-decoration: underline;
            padding: 0;
          }
          .footer-links span {
            color: #333;
          }
        `}</style>
      </div>

      {trailerOpen && show && show.trailer && (
        <TrailerModal
          trailer={show.trailer}
          showTitle={show.title}
          onClose={() => setTrailerOpen(false)}
        />
      )}
    </>
  );
};

export default PublicShow;
